import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { X, Loader2, FileCheck2, ShieldCheck, BookUser, ReceiptText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet transition-colors text-sm";
const REGIMENES = [["601", "601 · General PM"], ["612", "612 · PF Act. Empresarial"], ["626", "626 · RESICO"], ["603", "603 · PM sin fines de lucro"], ["616", "616 · Sin obligaciones fiscales"], ["621", "621 · Incorporación Fiscal"]];
const USOS = [["G01", "G01 · Adquisición de mercancías"], ["G03", "G03 · Gastos en general"], ["I01", "I01 · Construcciones"], ["P01", "P01 · Por definir"], ["S01", "S01 · Sin efectos fiscales"]];
const FORMAS = [["01", "01 · Efectivo"], ["03", "03 · Transferencia"], ["04", "04 · Tarjeta de crédito"], ["28", "28 · Tarjeta de débito"], ["99", "99 · Por definir"]];
const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;

// props: source ("lead"|"studio"), leadId, studio, projectId, quote (raw con items), defaultName, defaultEmail, onClose, onDone
export default function QuoteInvoiceModal({ source, leadId, studio, projectId, quoteId, quote, defaultName, defaultEmail, onClose, onDone }) {
  const marginPct = Number(quote?.margin_pct || 0);
  const taxPct = Number(quote?.tax_pct || 0);
  const marginIncluded = !!quote?.margin_included;
  const taxIncluded = !!quote?.tax_included;

  // Sólo partidas facturables (no encabezados, con importe)
  const invoiceable = useMemo(() => (quote?.items || []).filter((it) => it.kind !== "header" && Number(it.qty) > 0 && Number(it.unit_price) > 0), [quote]);
  const [sel, setSel] = useState(() => new Set(invoiceable.filter((it) => !it.invoiced).map((it) => it.id)));

  const [f, setF] = useState({ legal_name: defaultName || "", tax_id: "", tax_system: "601", zip: "", email: defaultEmail || "",
    use: "G03", payment_form: "03", payment_method: "PUE", credit_days: 0, save_customer: true });
  const [stampNow, setStampNow] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  useEffect(() => { api.get("/facturacion/customers").then((r) => setCustomers(r.data || [])).catch(() => {}); }, []);

  const pickCustomer = (id) => {
    const c = customers.find((x) => x.id === id); if (!c) return;
    setF((s) => ({ ...s, legal_name: c.legal_name, tax_id: c.tax_id, tax_system: c.tax_system, zip: c.zip, email: c.email || s.email, use: c.default_use || s.use, credit_days: c.credit_days ?? s.credit_days }));
  };

  const toggle = (id) => setSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allIds = invoiceable.filter((it) => !it.invoiced).map((it) => it.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => sel.has(id));
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(allIds));

  // Precio unitario mostrado (con margen, sin IVA) para dar contexto
  const unitShown = (it) => {
    const eff = marginIncluded ? Number(it.unit_price) : Number(it.unit_price) * (1 + marginPct / 100);
    const sinIva = taxIncluded ? eff / (1 + taxPct / 100) : eff;
    return sinIva * (1 - Number(it.discount_pct || 0) / 100);
  };
  const selTotal = invoiceable.filter((it) => sel.has(it.id)).reduce((a, it) => a + unitShown(it) * Number(it.qty || 0), 0);
  const selTotalConIva = selTotal * (1 + taxPct / 100);

  const submit = async () => {
    if (sel.size === 0) { toast.error("Selecciona al menos una partida a facturar"); return; }
    if (!f.legal_name || !f.tax_id || !f.zip) { toast.error("Completa razón social, RFC y CP del receptor"); return; }
    if (stampNow && !window.confirm("¿Timbrar ante el SAT ahora? Se generará un CFDI (según el ambiente activo: Prueba o Real).")) return;
    setBusy(true);
    try {
      const body = {
        source, lead_id: leadId, studio, project_id: projectId, quote_id: quoteId,
        item_ids: Array.from(sel),
        client: f.legal_name.trim(), legal_name: f.legal_name.trim(), tax_id: f.tax_id.trim().toUpperCase(),
        tax_system: f.tax_system, zip: f.zip.trim(), email: f.email.trim(), use: f.use,
        payment_form: f.payment_form, payment_method: f.payment_method, credit_days: Number(f.credit_days) || 0,
        stamp: stampNow, send_email: sendEmail,
      };
      const { data } = await api.post("/facturacion/quote-invoice", body);
      if (f.save_customer) {
        api.post("/facturacion/customers", { legal_name: body.legal_name, tax_id: body.tax_id, tax_system: body.tax_system, zip: body.zip, email: body.email, default_use: body.use, credit_days: body.credit_days }).catch(() => {});
      }
      setRes(data);
      toast.success(data.stamped ? `CFDI timbrado · ${data.uuid || ""}` : "Factura creada en borrador");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo facturar"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-navy/50 backdrop-blur-sm" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()} data-testid="quote-invoice-modal" className="w-full max-w-2xl bg-cream rounded-2xl p-7 max-h-[92vh] overflow-y-auto thin-scroll">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <div className="overline text-navy/40 flex items-center gap-1.5"><ReceiptText className="w-4 h-4 text-violet" /> Facturar presupuesto</div>
            <h3 className="text-xl font-medium">CFDI 4.0 · timbrado real</h3>
          </div>
          <button onClick={onClose} data-testid="quote-invoice-close" className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>

        {res ? (
          <div className="space-y-4" data-testid="quote-invoice-result">
            <div className={`rounded-xl border p-4 ${res.stamped ? "bg-emerald-500/10 border-emerald-500/20" : "bg-violet/5 border-violet/20"}`}>
              <div className="flex items-center gap-2 font-medium">{res.stamped ? <FileCheck2 className="w-5 h-5 text-emerald-600" /> : <ReceiptText className="w-5 h-5 text-violet" />} {res.stamped ? "CFDI timbrado" : "Factura creada en borrador"}</div>
              <div className="text-sm text-navy/60 mt-2 space-y-1">
                <div>Folio interno: <b>#{res.number}</b> · {res.item_count} partida(s)</div>
                <div>Total: <b>{money(res.total)}</b></div>
                {res.uuid && <div className="font-mono text-xs break-all">UUID: {res.uuid}</div>}
                {res.stamped && <div>Ambiente: <b>{res.environment === "live" ? "REAL" : "Prueba"}</b></div>}
                {!res.stamped && <div className="text-xs text-navy/50">Queda lista en <b>Facturación → Facturas</b> para revisarla y timbrarla cuando quieras.</div>}
              </div>
            </div>
            <button onClick={onClose} className="rounded-full bg-navy text-white px-6 py-2.5 text-sm hover:opacity-90" data-testid="quote-invoice-done">Cerrar</button>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Selección de partidas */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-navy/60 uppercase tracking-wide">Partidas a facturar</div>
                <button onClick={toggleAll} className="text-xs text-violet hover:underline" data-testid="qi-toggle-all">{allSelected ? "Quitar todas" : "Facturar todo"}</button>
              </div>
              <div className="rounded-xl border border-navy/10 divide-y divide-navy/5 max-h-56 overflow-y-auto thin-scroll">
                {invoiceable.length === 0 && <div className="p-4 text-sm text-navy/40">No hay partidas facturables. Guarda el presupuesto con conceptos y precios.</div>}
                {invoiceable.map((it) => (
                  <label key={it.id} className={`flex items-center gap-3 px-3 py-2.5 text-sm ${it.invoiced ? "opacity-50" : "cursor-pointer hover:bg-white/60"}`} data-testid={`qi-item-${it.id}`}>
                    <input type="checkbox" disabled={it.invoiced} checked={sel.has(it.id)} onChange={() => toggle(it.id)} data-testid={`qi-check-${it.id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{it.description || "Concepto"}</div>
                      <div className="text-[11px] text-navy/40">{Number(it.qty)} × {money(unitShown(it))}{it.unit ? ` · ${it.unit}` : ""}{it.product_key ? ` · SAT ${it.product_key}` : ""}{it.invoiced ? " · ya facturada" : ""}</div>
                    </div>
                    <div className="text-navy/60 shrink-0">{money(unitShown(it) * Number(it.qty || 0))}</div>
                  </label>
                ))}
              </div>
              <div className="text-right text-sm mt-2 text-navy/60">Subtotal seleccionado: <b>{money(selTotal)}</b> · con IVA aprox: <b className="text-navy">{money(selTotalConIva)}</b></div>
            </div>

            {/* Receptor */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-navy/60 uppercase tracking-wide">Datos del receptor</div>
                {customers.length > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-navy/50"><BookUser className="w-4 h-4" />
                    <select onChange={(e) => pickCustomer(e.target.value)} className="bg-white border border-navy/10 rounded-lg px-2 py-1 text-xs outline-none" data-testid="qi-customer-select">
                      <option value="">Cliente frecuente…</option>
                      {customers.map((c) => <option key={c.id} value={c.id}>{c.legal_name}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <input placeholder="Razón social" value={f.legal_name} onChange={(e) => setF({ ...f, legal_name: e.target.value })} className={input} data-testid="qi-legal-name" />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="RFC" value={f.tax_id} onChange={(e) => setF({ ...f, tax_id: e.target.value })} className={input} data-testid="qi-tax-id" />
                <input placeholder="Código postal" value={f.zip} onChange={(e) => setF({ ...f, zip: e.target.value })} className={input} data-testid="qi-zip" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select value={f.tax_system} onChange={(e) => setF({ ...f, tax_system: e.target.value })} className={input} data-testid="qi-regimen">{REGIMENES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                <select value={f.use} onChange={(e) => setF({ ...f, use: e.target.value })} className={input} data-testid="qi-use">{USOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <select value={f.payment_form} onChange={(e) => setF({ ...f, payment_form: e.target.value })} className={input} data-testid="qi-payment-form">{FORMAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                <select value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value })} className={input} data-testid="qi-payment-method"><option value="PUE">PUE · Pago en una exhibición</option><option value="PPD">PPD · Pago en parcialidades</option></select>
                <input type="number" placeholder="Días de crédito" value={f.credit_days} onChange={(e) => setF({ ...f, credit_days: e.target.value })} className={input} data-testid="qi-credit-days" />
              </div>
              <input placeholder="Correo del cliente (opcional)" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={input} data-testid="qi-email" />
            </div>

            {/* Opciones */}
            <div className="rounded-xl border border-navy/10 p-3 space-y-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="qi-stamp-now">
                <input type="checkbox" checked={stampNow} onChange={(e) => setStampNow(e.target.checked)} />
                <ShieldCheck className="w-4 h-4 text-violet" /> Timbrar ahora (si no, queda en borrador)
              </label>
              {stampNow && (
                <label className="flex items-center gap-2 text-sm cursor-pointer pl-6" data-testid="qi-send-email">
                  <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} /> Enviar el CFDI por correo al cliente
                </label>
              )}
              <label className="flex items-center gap-2 text-xs text-navy/60 cursor-pointer" data-testid="qi-save-customer">
                <input type="checkbox" checked={f.save_customer} onChange={(e) => setF({ ...f, save_customer: e.target.checked })} /> Guardar receptor como cliente frecuente
              </label>
            </div>

            <button onClick={submit} disabled={busy} className="w-full rounded-full bg-gradient-violet text-white px-6 py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="qi-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ReceiptText className="w-4 h-4" />} {stampNow ? "Facturar y timbrar" : "Crear factura (borrador)"}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
